import { ApplicationConfig, isDevMode } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { authInterceptor } from './interceptors/auth.interceptor';
import { ssrApiInterceptor } from './interceptors/ssr-api.interceptor';
import { environment } from '../environments/environment';
import { FCM_CONFIG } from 'briteatlib';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

const isBrowser = typeof window !== 'undefined';

// Firebase Cloud Messaging is a browser-only capability (it relies on the
// ServiceWorker / Notification APIs). Providing it during server-side rendering
// makes `getMessaging()` throw, so it is only registered in the browser. Auth is
// kept on both platforms because the HTTP interceptor and UserService inject it.
const messagingProviders = isBrowser
  ? [provideMessaging(() => getMessaging())]
  : [];

// PrimeNG components such as the payment accordion require the animation renderer to be
// available synchronously. Register it in the browser only so server-side rendering keeps
// the default (noop) animation behaviour without accessing the global `document`.
const animationProviders = isBrowser
  ? [provideAnimations()]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes,
      withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideClientHydration(),
    provideHttpClient(
      withFetch(),
      // ssrApiInterceptor is a no-op in the browser (SSR_API_ORIGIN is only bound on
      // the server); it runs first so the URL is absolute before authInterceptor.
      withInterceptors([ssrApiInterceptor, authInterceptor])
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    ...messagingProviders,
    { provide: FCM_CONFIG, useValue: environment },
    // A single, eager animations provider (browser only) keeps PrimeNG interactions working.
    ...animationProviders,
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { maxWidth: 'none' } }, // default configuration for the MatDialog component
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ],
};
